import {
  Button,
  Flex,
  Link,
  Popover,
  Typography,
  VerifiedIcon,
} from '@iheartjane/reefer';
import { Form } from '@iheartjane/reefer-hook-form';
import { clsx } from 'clsx';
import { useRef, useState } from 'react';

import { BloomLink } from '@jane/bloom/components/bloomLink';
import { FeelingIconMap } from '@jane/bloom/components/iconMaps/feelingIconMap';
import { ReeferCompatForm } from '@jane/bloom/components/reeferCompatForm';
import { ReviewStarIcons } from '@jane/bloom/components/reviewStars/reviewStars';
import { useReportReviewFetcher } from '@jane/bloom/hooks/useProductReviewReportFetcher';
import { useRouteWithRedirect } from '@jane/bloom/hooks/useRedirectRoute';
import { useRootLoaderData } from '@jane/bloom/hooks/useRootLoaderData';
import type { ProductReviewViewModel } from '@jane/bloom/viewModels/productReviews';

import styles from './productReview.module.css';

const reportOptions = [
  {
    id: 'nothelpful',
    label: 'Not helpful',
    value: 'not_helpful',
  },
  {
    id: 'spam',
    label: 'Looks like spam',
    value: 'spam',
  },
  {
    id: 'inappropriate',
    label: 'Inappropriate',
    value: 'inappropriate',
  },
  {
    id: 'other',
    label: 'Other reason',
    value: 'other',
  },
];

type ProductReviewReportPopoverProps = {
  productId: number;
  productSlug: string | undefined;
  reviewId: number;
};

const ProductReviewReportPopover = ({
  productId,
  productSlug,
  reviewId,
}: ProductReviewReportPopoverProps) => {
  const [report, setReport] = useState({
    reportDetailField: '',
    reportOptions: '',
  });

  const { handleSendReport } = useReportReviewFetcher({
    productId,
    productSlug,
  });

  const submitReport = async () => {
    handleSendReport({
      comment: report.reportDetailField,
      productReviewId: String(reviewId),
      reason: report.reportOptions,
    });
  };

  const scrollToTopOfReviewReport = () => {
    setTimeout(() => {
      if (reportRef.current) {
        const rect = reportRef.current.getBoundingClientRect();

        if (rect.top < 50) {
          window.scrollTo({
            behavior: 'smooth',
            top: rect.top + window.scrollY - 40,
          });
        }
      }
    }, 100);
  };

  const reportRef = useRef<HTMLDivElement>(null);

  return (
    <>
      <Popover
        target={
          <Link ml="auto" onClick={() => scrollToTopOfReviewReport()}>
            <Typography variant="small-body-regular" color="text-main">
              Report
            </Typography>
          </Link>
        }
        label="Report review"
        alignment={{ horizontal: 'right', vertical: 'top' }}
      >
        {({ closePopover }) => (
          <Popover.Content label="Report review form">
            <div
              className={styles['pdpReviewReportFormContainer']}
              ref={reportRef}
            >
              <ReeferCompatForm
                name="report-review-form"
                onSubmit={() => {
                  submitReport();
                  closePopover();
                }}
              >
                <Typography variant="body-semibold" mb={16}>
                  Why do you want to report this review?
                </Typography>
                <Form.RadioFieldGroup
                  name="reportOptions"
                  options={reportOptions}
                  onChange={(val: string) =>
                    setReport({ ...report, reportOptions: val })
                  }
                  required
                />

                <Typography>Please tell us why in more detail</Typography>
                <Form.TextAreaField
                  disableMobileInputStyling
                  label=""
                  mb={16}
                  name="reportDetailField"
                  required
                  rows={4}
                  onChange={(val: string) =>
                    setReport({ ...report, reportDetailField: val })
                  }
                />

                <Typography color="grays-mid" mb={16}>
                  To ensure product reviews on Jane are trustworthy, all review
                  reports are manually verified for legitimacy.
                </Typography>

                <Form.SubmitButton label="Submit" />
                <Button onClick={closePopover} label="Cancel" ml={16} />
              </ReeferCompatForm>
            </div>
          </Popover.Content>
        )}
      </Popover>
    </>
  );
};

const SignInPopover = ({ label, title }: { label: string; title: string }) => {
  const getRouteWithRedirect = useRouteWithRedirect();
  const route = getRouteWithRedirect('/login');

  return (
    <Popover
      target={
        <div className={styles['pdpReviewsPopoverLink']}>
          <Typography variant="small-body-regular" color="text-main">
            {title}
          </Typography>
        </div>
      }
      label={label}
      alignment={{ horizontal: 'right' }}
    >
      <Popover.Content label={label}>
        <div className={styles['pdpReviewsPopoverNotSignedIn']}>
          <BloomLink to={route}>
            <Typography>Please sign in.</Typography>
          </BloomLink>
        </div>
      </Popover.Content>
    </Popover>
  );
};

type ProductReviewProps = {
  productId: number;
  productSlug: string | undefined;
  review: ProductReviewViewModel;
  upvote: (reviewId: number) => void;
};

export const ProductReview = ({
  productId,
  productSlug,
  review,
  upvote,
}: ProductReviewProps) => {
  const {
    whoami: {
      user: { email: userEmail },
    },
  } = useRootLoaderData();

  const [showExpanded, setShowExpanded] = useState(false);
  const [userHasClicked, setUserHasClicked] = useState(false);
  const [helpfulCount, setHelpfulCount] = useState(review.upvoteCount ?? 0);

  const onHelpfulClick = () => {
    setUserHasClicked(true);
    setHelpfulCount(helpfulCount + 1);
    upvote(review.id);
  };

  return (
    <div className={styles['pdpReviewWrapper']}>
      <Flex>
        <div>
          <Flex alignItems="center">
            <ReviewStarIcons aggregateRating={Number(review.rating)} />
            <Typography ml={8} color="text-main">
              {review.rating}
            </Typography>
          </Flex>

          <Flex alignItems="center" gap={8} mt={8}>
            <VerifiedIcon size="sm" />
            <Typography color="grays-mid" variant="small-body-regular">
              {`Verified purchase, ${review.createdAt}`}
            </Typography>
          </Flex>
        </div>

        <Typography ml="auto" color="grays-mid">
          {review.nickname}
        </Typography>
      </Flex>

      <Typography color="text-main">
        {showExpanded ? review.comment : review.commentShort}
      </Typography>

      <Flex gap={16} flexWrap="wrap">
        {(showExpanded ? (review.feelings ?? []) : review.feelingsShort).map(
          (feeling) => (
            <Flex key={feeling} alignItems="center">
              {FeelingIconMap[feeling]}
              <Typography ml={8} variant="small-body-regular" color="text-main">
                {feeling}
              </Typography>
            </Flex>
          )
        )}
      </Flex>

      <div
        className={clsx({
          [styles['pdpReviewsLinkContainer']]: !review.canExpand,
          [styles['pdpReviewsLinkContainerFull']]: review.canExpand,
        })}
      >
        {review.canExpand && (
          <Link onClick={() => setShowExpanded(!showExpanded)} mr={16}>
            <Typography variant="small-body-regular" color="text-main">
              View {showExpanded ? 'less' : 'more'}
            </Typography>
          </Link>
        )}
        <div className={styles['pdpReviewsLinkButtons']}>
          {userEmail ? (
            <Link
              onClick={userHasClicked ? undefined : onHelpfulClick}
              ml="auto"
            >
              <Typography variant="small-body-regular" color="text-main">
                Helpful {helpfulCount > 0 && ` (${helpfulCount})`}
              </Typography>
            </Link>
          ) : (
            <SignInPopover title="Helpful" label="Helpful review" />
          )}

          {userEmail ? (
            <ProductReviewReportPopover
              reviewId={review.id}
              productId={productId}
              productSlug={productSlug}
            />
          ) : (
            <SignInPopover title="Report" label="Report review" />
          )}
        </div>
      </div>

      <hr className={styles['pdpReviewsActivitiesDividerMobile']} />
    </div>
  );
};
